/**
 * SOLID Review
 * S — Strong. One concern: the client's two stores — the table's and the session's — built empty, the table's with the one action
 *     `ClientState.apply` asks for first when a decision resets it.
 * O — Good. A new field is one member of its concern's state interface and one value in `EmptyClientState`; nothing here changes.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two stores.
 * D — Strong. zustand's vanilla store, the Application's store types and starting data, and the clock it is handed.
 */
import { createStore, type StoreApi } from 'zustand/vanilla';
import { EmptyClientState } from '../../application/state/EmptyClientState';
import type { GameStore } from '../../application/state/gameStoreTypes';
import type { IStateSessionHost } from '../../application/state/sessionStoreTypes';
import type { IServiceClock } from '../platform/IServiceClock';

/**
 * THE CLIENT'S TWO STORES (frontend-infrastructure-oo P4, 2026-10-29, §28.289) — built once by the composition root and handed to
 * `ClientStateStore` as the host's `tableStore` and `sessionStore`; React reads them through `ClientStateView` only.
 *
 * They were two module singletons, `useGameStore` and `useAuthStore`, made with zustand's React `create` at import — a hook nothing
 * called as one, and a store any module could import and write. Now the composition root holds the only reference, so the one
 * writer the stores have, `ClientState.apply`, is the only one any code can reach: the invariant is structural, not a convention.
 *
 * THE TABLE starts empty, stamped with the clock's time when the stores are built (the module's load until P4, as the slices'
 * `Date.now()` was before it), and `resetAll` restores that same stamp. THE SESSION starts with nobody, on the welcome screen.
 */
export class ClientStores {
  /** The table: the game store's data, and `resetAll` — back to the empty table the store started as. */
  readonly table: StoreApi<GameStore>;
  /** The session's account, screen, funnel fields, consent, ad config and video chat. */
  readonly session: StoreApi<IStateSessionHost>;

  constructor(clock: IServiceClock) {
    const empty = new EmptyClientState();
    const startedAt = clock.now();
    this.table = createStore<GameStore>()((set) => ({
      ...empty.game(startedAt),

      resetAll() {
        set(empty.game(startedAt));
      },
    }));
    this.session = createStore<IStateSessionHost>()(() => empty.sessionHost());
  }
}
